import {
  PathPickMarket,
  PathPickMarketOption,
  PathPickMarketPriceBasis,
  PathPickPoizonOption,
  PathPickProduct,
} from "@/features/pathpick/types";
import {
  getMarketPriceByBasis,
  getPathPickMargin,
  getPathPickMarketRecommendation,
  isPathPickMarketMarginAvailable,
  isPathPickMarketMatched,
  PathPickMargin,
  PathPickMarketRecommendation,
} from "@/features/pathpick/utils/market";

// 옵션 표의 그룹 — 선언 순서가 표의 그룹 순서다.
//   pathrade : 공급처(패스레이드) 옵션. 재고·주문의 기준이 되는 행이다.
//   poizon   : 패스레이드 옵션과 매칭되지 않은 포이즌 사이즈(크림에도 같은 사이즈가 있으면 그 값까지 채운다)
//   kream    : 패스레이드 옵션과 매칭되지 않은 크림 사이즈(포이즌에도 같은 사이즈가 있으면 그 값까지 채운다)
export type PathPickOptionGroupKey = "pathrade" | "poizon" | "kream";

// 패스레이드 옵션 1개가 두 마켓 중 어디에 붙었는지.
export type PathPickOptionMatchState =
  | "matched"
  | "kreamOnly"
  | "poizonOnly"
  | "unmatched";

// 옵션 1개 × 마켓 1개 셀. isMatched=false면 값 칸을 "미매칭"/"-"으로 그린다.
export interface PathPickOptionMarketCell extends PathPickMargin {
  market: PathPickMarket;
  isMatched: boolean;
  lowestValue: number | null;
  lastValue: number | null;
  // 포이즌만 옵션 단위 판매량(sales_30d)을 준다. 크림 옵션 응답에는 없어 상품 레벨
  // 누적 체결수를 그대로 보여준다 — 사이즈별로 갈라 보여줄 근거가 없다.
  salesCount: number | null;
  basisValue: number | null;
  // false면 매칭이어도 마진 칸이 "계산 불가" — 크림 체결수가 적어 상품 레벨에서 막힌 경우다
  // (사이즈별 시세는 실시간으로 받아 오지만, 마진 판단 기준인 체결수는 상품 단위라 같이 막는다).
  isMarginAvailable: boolean;
}

export interface PathPickProductOptionRow {
  key: string;
  label: string;
  group: PathPickOptionGroupKey;
  // 패스레이드 옵션만 재고가 있다. 마켓 전용 옵션은 null → "-"
  stock: number | null;
  matchState: PathPickOptionMatchState;
  kream: PathPickOptionMarketCell;
  poizon: PathPickOptionMarketCell;
  // 패스레이드 옵션이면서 양쪽 마진이 다 있을 때만 채워진다.
  // 마켓 전용 그룹 행은 주문 대상이 아니라 항상 null이다(추천 칸에 "미매칭"을 그린다).
  recommendation: PathPickMarketRecommendation | null;
}

export interface PathPickOptionGroup {
  key: PathPickOptionGroupKey;
  rows: PathPickProductOptionRow[];
}

// 마켓별 매칭 집계 — 상태 배너가 이 수치를 그대로 보여준다.
// 세는 대상은 **패스레이드 옵션**이다. 배너가 알리려는 것이 "이 옵션들은 시세를 못 붙였다"라서,
// 마켓 전용 옵션이 몇 개든 기준은 주문 가능한 패스레이드 옵션이어야 한다.
export interface PathPickMarketMatchCount {
  matched: number;
  unmatched: number;
  // 그 마켓이 내려준 옵션 수 — 0이면 "상품 미매칭"으로 가른다.
  optionCount: number;
  // 상품 자체가 그 마켓에 붙어 있는지(시세가 하나라도 있는지)
  hasProduct: boolean;
}

export interface PathPickProductOptionTableModel {
  // 행이 하나도 없는 그룹은 빠진다(그래서 헤더까지 통째로 사라진다).
  groups: PathPickOptionGroup[];
  matchCounts: Record<PathPickMarket, PathPickMarketMatchCount>;
}

// 옵션명 정규화 — 공백을 지우고 소문자로 맞춰 비교한다("ONE SIZE" vs "onesize").
const toOptionKey = (option: { name: string; displayName: string }) =>
  (option.displayName || option.name).replace(/\s+/g, "").toLowerCase();

const toOptionLabel = (option: { name: string; displayName: string }) =>
  (option.displayName || option.name).toUpperCase();

const toMarketMap = <TOption extends PathPickMarketOption>(
  options: TOption[]
) => new Map(options.map((option) => [toOptionKey(option), option]));

const EMPTY_CELL = (market: PathPickMarket): PathPickOptionMarketCell => ({
  market,
  isMatched: false,
  lowestValue: null,
  lastValue: null,
  salesCount: null,
  basisValue: null,
  // 미매칭 셀은 애초에 마진이 없다 — "계산 불가" 대신 "-"로 그리도록 true 로 둔다.
  isMarginAvailable: true,
  expectedMargin: null,
  marginRate: null,
});

const toMarketCell = (
  market: PathPickMarket,
  option: PathPickMarketOption | PathPickPoizonOption | undefined,
  basis: PathPickMarketPriceBasis,
  supplyPrice: number,
  // 옵션 응답이 판매량을 주지 않는 마켓(크림)에서 쓸 상품 레벨 값
  fallbackSalesCount: number | null,
  // 상품 레벨 마진 가능 여부(크림 체결수 부족이면 false) — 카드·상세와 같은 기준을 쓴다.
  isMarginAvailable: boolean
): PathPickOptionMarketCell => {
  if (!option) {
    return EMPTY_CELL(market);
  }
  const basisValue = getMarketPriceByBasis(option.price, basis);

  return {
    market,
    isMatched: true,
    lowestValue: option.price.lowestValue,
    lastValue: option.price.lastValue,
    // 키가 snake인 이유는 PathPickPoizonOption 선언부 주석 참고 — 문자열 리터럴 검사라
    // 철자가 어긋나도 타입 에러 없이 조용히 fallback(포이즌은 null)으로 떨어진다.
    salesCount: "sales_30d" in option ? option.sales_30d : fallbackSalesCount,
    basisValue,
    isMarginAvailable,
    ...getPathPickMargin(isMarginAvailable ? basisValue : null, supplyPrice),
  };
};

const toMatchState = (
  hasKream: boolean,
  hasPoizon: boolean
): PathPickOptionMatchState => {
  if (hasKream && hasPoizon) return "matched";
  if (hasKream) return "kreamOnly";
  if (hasPoizon) return "poizonOnly";
  return "unmatched";
};

/**
 * 패스레이드 옵션 + 크림/포이즌 옵션을 옵션명으로 머지해 표 모델을 만든다.
 *
 * 크림·포이즌 옵션 응답은 상세 모달을 열 때 각각 따로 오므로, 한쪽만 먼저 도착한 상태로도
 * 호출될 수 있다 — 그때는 그 마켓 셀이 비어 있는 표가 나온다(호출부가 조회 중이면 표 대신
 * 스켈레톤을 그려 "아직 모르는 것"을 "미매칭"으로 단정하지 않는다).
 */
export const buildPathPickProductOptionTable = ({
  item,
  kreamOptions,
  poizonOptions,
  basis,
}: {
  item: PathPickProduct;
  kreamOptions: PathPickMarketOption[];
  poizonOptions: PathPickPoizonOption[];
  basis: PathPickMarketPriceBasis;
}): PathPickProductOptionTableModel => {
  const supplyPrice = item.salePrice.value;
  const kreamByKey = toMarketMap(kreamOptions);
  const poizonByKey = toMarketMap(poizonOptions);
  // 크림 옵션 응답에는 사이즈별 체결수가 없어 상품 레벨 누적 체결수로 채운다.
  const kreamSalesCount = item.kream?.salesCount ?? null;
  // 마진 가능 여부도 상품 레벨 판정을 그대로 쓴다 — 카드에서 "계산 불가"인 상품이
  // 옵션 표에서만 마진을 내면 같은 상품이 화면마다 다르게 보인다.
  const isKreamMarginAvailable = isPathPickMarketMarginAvailable(item, "KREAM");

  const matchCounts: Record<PathPickMarket, PathPickMarketMatchCount> = {
    KREAM: {
      matched: 0,
      unmatched: 0,
      optionCount: kreamOptions.length,
      hasProduct: isPathPickMarketMatched(item, "KREAM"),
    },
    POIZON: {
      matched: 0,
      unmatched: 0,
      optionCount: poizonOptions.length,
      hasProduct: isPathPickMarketMatched(item, "POIZON"),
    },
  };

  const pathradeRows = (item.options ?? []).map((option) => {
    const key = toOptionKey(option);
    const kreamOption = kreamByKey.get(key);
    const poizonOption = poizonByKey.get(key);

    matchCounts.KREAM[kreamOption ? "matched" : "unmatched"] += 1;
    matchCounts.POIZON[poizonOption ? "matched" : "unmatched"] += 1;

    const kream = toMarketCell(
      "KREAM",
      kreamOption,
      basis,
      supplyPrice,
      kreamSalesCount,
      isKreamMarginAvailable
    );
    const poizon = toMarketCell(
      "POIZON",
      poizonOption,
      basis,
      supplyPrice,
      null,
      true
    );

    return {
      key,
      label: toOptionLabel(option),
      group: "pathrade" as const,
      stock: option.stock,
      matchState: toMatchState(Boolean(kreamOption), Boolean(poizonOption)),
      kream,
      poizon,
      recommendation: getPathPickMarketRecommendation(kream, poizon),
    };
  });

  // 마켓 전용 그룹 — 패스레이드 옵션과 매칭되지 않은 사이즈만 남긴다.
  const isPathradeOption = (key: string) =>
    pathradeRows.some((row) => row.key === key);

  const toMarketOnlyRows = (
    market: PathPickMarket,
    options: (PathPickMarketOption | PathPickPoizonOption)[]
  ): PathPickProductOptionRow[] =>
    options
      .filter((option) => !isPathradeOption(toOptionKey(option)))
      .map((option) => {
        const optionKey = toOptionKey(option);
        // 패스레이드에 없는 사이즈라도 두 마켓끼리는 옵션명이 맞을 수 있다 — 반대쪽 마켓도 같은 키로
        // 찾아 한 행에 나란히 채운다. 표의 기준은 여전히 패스레이드라 매칭 상태는 "미매칭" 그대로다.
        const kream = toMarketCell(
          "KREAM",
          kreamByKey.get(optionKey),
          basis,
          supplyPrice,
          kreamSalesCount,
          isKreamMarginAvailable
        );
        const poizon = toMarketCell(
          "POIZON",
          poizonByKey.get(optionKey),
          basis,
          supplyPrice,
          null,
          true
        );

        return {
          key: `${market.toLowerCase()}-${optionKey}`,
          label: toOptionLabel(option),
          group: (market === "KREAM" ? "kream" : "poizon") as
            | "kream"
            | "poizon",
          // 공급처에 없는 사이즈라 구매가능재고가 없다.
          stock: null,
          matchState: "unmatched" as const,
          kream,
          poizon,
          // 주문할 수 없는 사이즈라 추천 대상이 아니다.
          recommendation: null,
        };
      });

  const allGroups: PathPickOptionGroup[] = [
    { key: "pathrade", rows: pathradeRows },
    { key: "poizon", rows: toMarketOnlyRows("POIZON", poizonOptions) },
    { key: "kream", rows: toMarketOnlyRows("KREAM", kreamOptions) },
  ];
  // 행이 없는 그룹은 헤더까지 통째로 빠진다.
  const groups = allGroups.filter((group) => group.rows.length > 0);

  return { groups, matchCounts };
};

// 마켓별 상태 배너 — 낼 말이 없으면 null이라 그 마켓은 배너가 아예 안 그려진다.
//   unavailable      : 옵션 조회 실패/시간 초과 (없는 사실을 "미매칭"으로 단정하지 않는다)
//   productUnmatched : 그 마켓에 붙은 상품이 없거나 옵션을 하나도 주지 않았다
//   allUnmatched     : 옵션은 있는데 패스레이드 옵션과 하나도 매칭되지 않았다
//   partialUnmatched : 일부만 매칭됐다
export type PathPickMarketBannerCase =
  | "unavailable"
  | "productUnmatched"
  | "allUnmatched"
  | "partialUnmatched";

// 조회 상태 → 마켓 상품 연결 → 옵션 매칭 순으로 가른다. 앞선 조건이 뒤 조건의 전제라서
// 순서를 섞으면 답이 달라진다 — 그래서 중첩 삼항 대신 이른 반환으로 늘어놓는다.
export const getPathPickMarketBannerCase = ({
  isPending,
  isUnavailable,
  matchCount,
}: {
  isPending: boolean;
  isUnavailable: boolean;
  matchCount: PathPickMarketMatchCount;
}): PathPickMarketBannerCase | null => {
  // 조회 중에는 아직 어떤 케이스인지 알 수 없다 — 배너 자리는 스켈레톤이 채운다.
  if (isPending) {
    return null;
  }
  if (isUnavailable) {
    return "unavailable";
  }
  // 상품이 안 붙었거나 옵션을 하나도 못 받았으면 옵션명 문제가 아니다.
  if (!matchCount.hasProduct || matchCount.optionCount === 0) {
    return "productUnmatched";
  }
  // 남은 패스레이드 옵션이 없으면 마켓 전용 옵션이 몇 개든 알릴 것이 없다.
  // (옵션이 아예 없는 상품도 여기로 빠진다 — 매칭 0을 미매칭이라 단정하지 않는다)
  if (matchCount.unmatched === 0) {
    return null;
  }
  return matchCount.matched === 0 ? "allUnmatched" : "partialUnmatched";
};
